/*  src/views/customer/dashboard/CustomerDashboard.jsx */

import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch }   from 'react-redux';
import Swal                           from 'sweetalert2';

import {
  getCustomerCreditService,
  payCustomerCreditService,
} from '@/services/CustomerServices';
import validationErrors               from '@/lib/validationErrors';

import CreditOverviewComponent        from './component/CreditOverviewComponent';
import PayCreditButton                from './component/PayCreditButton';

const CustomerDashboard = () => {
  const dispatch = useDispatch();
  const customer = useSelector((s) => s.Auth.userData); // flattened shape

  /* ------------------ local state ------------------ */
  const [isLoading, setIsLoading] = useState(false);
  const [creditData, setCreditData] = useState({
    totalCredit: 0,
    usedCredit: 0,
    remainingCredit: 0,
  });

  /* ------------------ helpers ------------------ */
  const fetchCredit = (customerId) => {
    setIsLoading(true);
    dispatch(getCustomerCreditService({ customerId }))
      .then((res) => {
        const d = res?.data?.data ?? {};
        setCreditData({
          totalCredit:     d.totalCredit     ?? 0,
          usedCredit:      d.usedCredit      ?? 0,
          remainingCredit: d.remainingCredit ?? 0,
        });
      })
      .catch(({ message, errorData }) => {
        validationErrors(errorData, message);
        setCreditData({ totalCredit: 0, usedCredit: 0, remainingCredit: 0 });
      })
      .finally(() => setIsLoading(false));
  };

  /* first load + whenever logged-in customer changes */
  useEffect(() => {
    if (customer?._id) fetchCredit(customer._id);
  }, [customer]);

  /* ------------------ pay credit ------------------ */
  const handlePayCredit = () => {
    const outstanding = Number(creditData.usedCredit) || 0;
    if (!outstanding) {
      Swal.fire({
        icon: 'info',
        title: 'No Outstanding Credit',
        timer: 2000,
        showConfirmButton: false,
      });
      return;
    }

    Swal.fire({
      title: 'Pay Credit',
      text: `Pay your outstanding credit of ${outstanding}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, pay it!',
    }).then((r) => {
      if (!r.isConfirmed) return;
      setIsLoading(true);
      dispatch(payCustomerCreditService({ customerId: customer._id, amount: outstanding }))
        .then(() => {
          Swal.fire('Success', 'Your credit has been paid!', 'success');
          fetchCredit(customer._id); // refresh summary
        })
        .catch(({ message, errorData }) => validationErrors(errorData, message))
        .finally(() => setIsLoading(false));
    });
  };

  /* ------------------ render ------------------ */
  const hasOutstanding = Number(creditData.usedCredit) > 0;

  return (
    <div className="p-4">
      {/* greeting */}
      <h3 className="font-semibold text-[26px] leading-10 capitalize text-primary mb-8">
        Good&nbsp;Evening{customer?.name ? `, ${customer.name}` : ''}!
      </h3>

      {/* credit summary */}
      <CreditOverviewComponent creditData={creditData} isLoading={isLoading} />

      {/* pay button */}
      <div className="mt-4">
        <PayCreditButton
          isLoading={isLoading}
          onPayCredit={handlePayCredit}
          disabled={!hasOutstanding}
        />
      </div>
    </div>
  );
};

export default CustomerDashboard;
